import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import supabase from '../../utils/supabaseClient';
import LoadingScreen from '../../components/LoadingScreen';
import { Button, Box } from '@mui/material';
import { getUploadedSlides } from './GetUploadedSlides';
import PDFViewer from '../../components/PDFViewer';

// Flag to disable confusing warning messages
const DISABLE_WARNING_MESSAGES = true;

const GalleryViewPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [galleryPitch, setGalleryPitch] = useState(null);
  const [pitchDeck, setPitchDeck] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const slideInterval = useRef(null);
  const viewCountIncremented = useRef(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [fetchingPitchDeck, setFetchingPitchDeck] = useState(false);
  // Add state to track if we're displaying a PDF
  const [isDisplayingPDF, setIsDisplayingPDF] = useState(false);
  // Add a slidesArray ref to hold the slides data
  const slidesArrayRef = useRef([]);
  
  // Add useEffect for checking PDF content
  useEffect(() => {
    // Function to check if a slide contains PDF content
    const checkForPDFContent = (slide) => {
      if (!slide) return false;
      
      return (
        (slide.is_pdf && slide.pdf_url) ||
        (slide.images && 
         Array.isArray(slide.images) && 
         slide.images.some(img => img.url && img.url.toLowerCase().endsWith('.pdf')))
      );
    };
    
    // Get the current slide content from our slides array reference
    const currentSlideContent = slidesArrayRef.current[currentSlide] || null;
    
    // Update the isDisplayingPDF state based on current slide content
    setIsDisplayingPDF(checkForPDFContent(currentSlideContent));
  }, [currentSlide]); // Depend on currentSlide index instead of the content object itself
  
  // Memoize functions to use as dependencies in useEffect
  const fetchGalleryPitch = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch gallery pitch details with product data joined in
      const { data: galleryData, error: galleryError } = await supabase
        .from('gallery_pitches')
        .select(`
          *,
          products:product_id (id, name, development_stage, product_category, user_id)
        `)
        .eq('id', id)
        .single();

      if (galleryError) throw galleryError;
      if (!galleryData) throw new Error('Pitch deck not found');

      // Log the full gallery data for debugging
      console.log('Gallery pitch data retrieved:', { 
        id: galleryData.id, 
        title: galleryData.title,
        product_id: galleryData.product_id,
        user_id: galleryData.user_id,
        product_data: galleryData.products
      });

      // If the join didn't return product data, try fetching it directly
      if (!galleryData.products && galleryData.product_id) {
        try {
          console.log('Join failed to retrieve product data, trying direct fetch for ID:', galleryData.product_id);
          
          // Add a special flag to ensure public read access works properly
          const { data: productData, error: productError } = await supabase
            .from('products')
            .select('id, name, development_stage, product_category, user_id')
            .eq('id', galleryData.product_id)
            .maybeSingle();
          
          if (!productError && productData) {
            // Attach the product data to the gallery pitch
            galleryData.products = productData;
            console.log('Product data retrieved via direct fetch:', productData);
          } else {
            // Try a fallback approach with an RPC function (you'll need to create this in Supabase)
            try {
              console.log('Direct fetch failed, trying RPC function');
              const { data: publicProductData, error: publicError } = await supabase
                .rpc('get_product_public_data', { product_id: galleryData.product_id })
                .maybeSingle();
                
              if (!publicError && publicProductData) {
                galleryData.products = publicProductData;
                console.log('Product data retrieved using RPC:', publicProductData);
              } else {
                console.warn('All product fetch attempts failed:', publicError);
                // Set default product data
                galleryData.products = {
                  name: 'Product information unavailable',
                  development_stage: 'Not specified',
                  product_category: 'Not specified'
                };
              }
            } catch (rpcError) {
              console.error('RPC function error:', rpcError);
              // Set default product data
              galleryData.products = {
                name: 'Product information unavailable',
                development_stage: 'Not specified',
                product_category: 'Not specified'
              };
            }
          }
        } catch (directFetchError) {
          console.error('Error in direct product fetch:', directFetchError);
          galleryData.products = {
            name: 'Product information unavailable',
            development_stage: 'Not specified',
            product_category: 'Not specified'
          };
        }
      } else if (!galleryData.products) {
        console.warn('No product_id in gallery pitch');
        galleryData.products = {
          name: 'No product associated',
          development_stage: 'Not specified',
          product_category: 'Not specified'
        };
      }

      setGalleryPitch(galleryData);

      // Fetch user profile separately
      if (galleryData.user_id) {
        try {
          const { data: profileData, error: profileError } = await supabase
            .from('user_profiles')
            .select('name, company, job_title, photo_url')
            .eq('user_id', galleryData.user_id)
            .single();
            
          if (!profileError && profileData) {
            setUserProfile(profileData);
          } else {
            // Set default profile data if not found or error
            console.log('User profile fetch error or not found:', profileError);
            setUserProfile({
              name: 'Anonymous',
              job_title: 'No information available',
              company: '',
              photo_url: ''
            });
          }
        } catch (profileFetchError) {
          // Catch any unexpected errors during profile fetch
          console.error('Error fetching user profile:', profileFetchError);
          setUserProfile({
            name: 'Anonymous',
            job_title: 'No information available',
            company: '',
            photo_url: ''
          });
        }
      } else {
        // Set default profile when no user_id is present
        setUserProfile({
          name: 'Anonymous',
          job_title: 'No information available',
          company: '',
          photo_url: ''
        });
      }

      // Fetch pitch deck content
      try {
        // Log the pitch deck ID we're trying to fetch for debugging
        console.log(`Attempting to fetch pitch deck with ID: ${galleryData.pitch_deck_id}`);
        
        // Check if we have a valid UUID before attempting to query
        if (!galleryData.pitch_deck_id || galleryData.pitch_deck_id === 'null' || galleryData.pitch_deck_id === 'undefined') {
          console.warn('Invalid pitch deck ID:', galleryData.pitch_deck_id);
          // Create a default fallback pitch deck for invalid IDs
          setPitchDeck({
            id: 'fallback',
            title: galleryData.title || 'Pitch Deck',
            slides: [{
              title: 'No Slides Available',
              content: 'This pitch does not have associated slides or content.',
              key: 'slide-1',
              images: []
            }],
            content: {
              slides: [{
                title: 'No Slides Available',
                content: 'This pitch does not have associated slides or content.',
                key: 'slide-1',
                images: []
              }]
            }
          });
        } else {
          // Valid ID, attempt to fetch using a stored procedure that can bypass RLS
          try {
            // First try using an RPC function that bypasses RLS for public access
            const { data: publicPitchDeckData, error: publicPitchDeckError } = await supabase
              .rpc('get_pitch_deck_public_data', { deck_id: galleryData.pitch_deck_id })
              .maybeSingle();
              
            if (!publicPitchDeckError && publicPitchDeckData) {
              console.log('Successfully fetched pitch deck via RPC:', publicPitchDeckData?.id);
              console.log('RPC response structure:', JSON.stringify(publicPitchDeckData).substring(0, 200) + '...');
              
              // ADDED MORE DETAILED LOGGING
              console.log('DETAILED DATA INSPECTION:');
              console.log('- Full response keys:', Object.keys(publicPitchDeckData));
              console.log('- Content type:', typeof publicPitchDeckData.content);
              if (publicPitchDeckData.content) {
                if (typeof publicPitchDeckData.content === 'string') {
                  console.log('- Content is string, first 100 chars:', publicPitchDeckData.content.substring(0, 100));
                  try {
                    const parsedContent = JSON.parse(publicPitchDeckData.content);
                    console.log('- Parsed content keys:', Object.keys(parsedContent));
                    if (parsedContent.slides) {
                      console.log('- Content has slides property of type:', typeof parsedContent.slides);
                      if (Array.isArray(parsedContent.slides)) {
                        console.log('- Content slides count:', parsedContent.slides.length);
                        if (parsedContent.slides.length > 0) {
                          console.log('- First slide keys:', Object.keys(parsedContent.slides[0]));
                        }
                      } else if (typeof parsedContent.slides === 'object') {
                        console.log('- Content slides keys:', Object.keys(parsedContent.slides));
                      }
                    }
                  } catch (e) {
                    console.log('- Content string is not valid JSON');
                  }
                } else if (typeof publicPitchDeckData.content === 'object') {
                  console.log('- Content object keys:', Object.keys(publicPitchDeckData.content));
                  if (publicPitchDeckData.content.slides) {
                    console.log('- Content has slides property of type:', typeof publicPitchDeckData.content.slides);
                    if (Array.isArray(publicPitchDeckData.content.slides)) {
                      console.log('- Content slides count:', publicPitchDeckData.content.slides.length);
                      if (publicPitchDeckData.content.slides.length > 0) {
                        console.log('- First slide keys:', Object.keys(publicPitchDeckData.content.slides[0]));
                      }
                    } else if (typeof publicPitchDeckData.content.slides === 'object') {
                      console.log('- Content slides keys:', Object.keys(publicPitchDeckData.content.slides));
                    }
                  }
                }
              }
              
              if (publicPitchDeckData.slides) {
                console.log('- Direct slides type:', typeof publicPitchDeckData.slides);
                if (Array.isArray(publicPitchDeckData.slides)) {
                  console.log('- Direct slides count:', publicPitchDeckData.slides.length);
                  if (publicPitchDeckData.slides.length > 0) {
                    console.log('- First direct slide keys:', Object.keys(publicPitchDeckData.slides[0]));
                    console.log('- First direct slide sample:', JSON.stringify(publicPitchDeckData.slides[0]).substring(0, 100));
                  }
                } else if (typeof publicPitchDeckData.slides === 'object') {
                  console.log('- Direct slides object keys:', Object.keys(publicPitchDeckData.slides));
                  console.log('- Direct slides object sample:', JSON.stringify(publicPitchDeckData.slides).substring(0, 100));
                }
              }
              // END DETAILED LOGGING
              
              // Directly use the data without any complex processing
              try {
                // Create a minimal copy with safe defaults
                const safeData = {
                  id: publicPitchDeckData.id || 'fallback',
                  title: publicPitchDeckData.name || galleryData.title || 'Pitch Deck',
                  content: {},
                  slides: []
                };
                
                // Log what's in the RPC response
                console.log('RPC data format analysis:', {
                  hasContent: !!publicPitchDeckData.content,
                  contentType: typeof publicPitchDeckData.content,
                  hasSlides: !!publicPitchDeckData.slides,
                  slidesType: typeof publicPitchDeckData.slides,
                  isSlideArray: Array.isArray(publicPitchDeckData.slides),
                  slidesSampleData: publicPitchDeckData.slides ? 
                    (Array.isArray(publicPitchDeckData.slides) ? 
                      `Array with ${publicPitchDeckData.slides.length} items` : 
                      `Object with keys: ${Object.keys(publicPitchDeckData.slides).join(', ')}`) : 
                    'No slides data'
                });
                
                // Safely add content if it exists
                if (publicPitchDeckData.content) {
                  if (typeof publicPitchDeckData.content === 'string') {
                    try {
                      safeData.content = JSON.parse(publicPitchDeckData.content);
                    } catch (e) {
                      console.error('Content parsing error:', e);
                      safeData.content = {};
                    }
                  } else {
                    safeData.content = publicPitchDeckData.content;
                  }
                }
                
                // IMPROVED SLIDE EXTRACTION LOGIC
                // First try direct slides from RPC response
                if (publicPitchDeckData.slides) {
                  if (Array.isArray(publicPitchDeckData.slides)) {
                    console.log(`Direct slides array found with ${publicPitchDeckData.slides.length} items`);
                    
                    if (publicPitchDeckData.slides.length > 0) {
                      // Using proper deep copy to ensure we don't mutate the original data
                      safeData.slides = publicPitchDeckData.slides.map(slide => ({
                        title: slide.title || 'Untitled',
                        content: slide.content || '',
                        key: slide.key || `slide-${Math.random().toString(36).substr(2, 9)}`,
                        images: Array.isArray(slide.images) ? [...slide.images] : []
                      }));
                      
                      console.log(`Processed ${safeData.slides.length} slides from direct array`);
                    }
                  } else if (typeof publicPitchDeckData.slides === 'object' && publicPitchDeckData.slides !== null) {
                    // Check if this is a slide collection object (non-null object that's not an array)
                    console.log('Slides is an object, extracting slide data from keys');
                    
                    const slideKeys = Object.keys(publicPitchDeckData.slides).filter(key => 
                      key !== 'slide_order' && 
                      key !== 'updated_at' && 
                      key !== 'background_fit' &&
                      key !== 'background_image'
                    );
                    
                    if (slideKeys.length > 0) {
                      safeData.slides = slideKeys.map(key => {
                        const slide = publicPitchDeckData.slides[key];
                        return {
                          title: (slide && slide.title) || key,
                          content: (slide && slide.content) || '',
                          key: key,
                          images: (slide && Array.isArray(slide.images)) ? [...slide.images] : []
                        };
                      });
                      
                      console.log(`Processed ${safeData.slides.length} slides from object keys`);
                    }
                  } else if (typeof publicPitchDeckData.slides === 'string') {
                    // Try parsing string data
                    console.log('Slides is a string, attempting to parse as JSON');
                    try {
                      const parsedSlides = JSON.parse(publicPitchDeckData.slides);
                      
                      if (Array.isArray(parsedSlides)) {
                        console.log(`Parsed slides string into array with ${parsedSlides.length} items`);
                        if (parsedSlides.length > 0) {
                          safeData.slides = parsedSlides.map(slide => ({
                            title: slide.title || 'Untitled',
                            content: slide.content || '',
                            key: slide.key || `slide-${Math.random().toString(36).substr(2, 9)}`,
                            images: Array.isArray(slide.images) ? [...slide.images] : []
                          }));
                        }
                      }
                    } catch (e) {
                      console.error('Error parsing slides string:', e);
                    }
                  }
                }
                
                // If no slides yet, check content.slides
                if (safeData.slides.length === 0 && safeData.content) {
                  console.log('No slides from direct property, checking content.slides');
                  
                  if (safeData.content.slides) {
                    if (Array.isArray(safeData.content.slides)) {
                      console.log(`Found slides array in content with ${safeData.content.slides.length} items`);
                      
                      if (safeData.content.slides.length > 0) {
                        safeData.slides = safeData.content.slides.map(slide => ({
                          title: slide.title || 'Untitled',
                          content: slide.content || '',
                          key: slide.key || `slide-${Math.random().toString(36).substr(2, 9)}`,
                          images: Array.isArray(slide.images) ? [...slide.images] : []
                        }));
                      }
                    } else if (typeof safeData.content.slides === 'object' && safeData.content.slides !== null) {
                      console.log('Content.slides is an object, extracting slide data from keys');
                      
                      const slideKeys = Object.keys(safeData.content.slides).filter(key => 
                        key !== 'slide_order' && 
                        key !== 'updated_at' && 
                        key !== 'background_fit' &&
                        key !== 'background_image'
                      );
                      
                      if (slideKeys.length > 0) {
                        safeData.slides = slideKeys.map(key => {
                          const slide = safeData.content.slides[key];
                          return {
                            title: (slide && slide.title) || key,
                            content: (slide && slide.content) || '',
                            key: key,
                            images: (slide && Array.isArray(slide.images)) ? [...slide.images] : []
                          };
                        });
                      }
                    }
                  }
                }
                
                // Last resort - try to parse raw content string for slides data
                if (safeData.slides.length === 0 && typeof publicPitchDeckData.content === 'string') {
                  console.log('No slides found yet, attempting raw content string parsing');
                  
                  try {
                    const rawContent = JSON.parse(publicPitchDeckData.content);
                    
                    if (rawContent) {
                      if (Array.isArray(rawContent)) {
                        // Content itself might be an array of slides
                        console.log('Content parsed to an array, treating as slides');
                        
                        if (rawContent.length > 0) {
                          safeData.slides = rawContent.map(slide => ({
                            title: slide.title || 'Untitled',
                            content: slide.content || '',
                            key: slide.key || `slide-${Math.random().toString(36).substr(2, 9)}`,
                            images: Array.isArray(slide.images) ? [...slide.images] : []
                          }));
                        }
                      } else if (rawContent.slides) {
                        // Check if the content has a slides property
                        if (Array.isArray(rawContent.slides)) {
                          console.log(`Found slides array in parsed content with ${rawContent.slides.length} items`);
                          
                          if (rawContent.slides.length > 0) {
                            safeData.slides = rawContent.slides.map(slide => ({
                              title: slide.title || 'Untitled',
                              content: slide.content || '',
                              key: slide.key || `slide-${Math.random().toString(36).substr(2, 9)}`,
                              images: Array.isArray(slide.images) ? [...slide.images] : []
                            }));
                          }
                        } else if (typeof rawContent.slides === 'object' && rawContent.slides !== null) {
                          console.log('Parsed content.slides is an object, extracting slide data from keys');
                          
                          const slideKeys = Object.keys(rawContent.slides).filter(key => 
                            key !== 'slide_order' && 
                            key !== 'updated_at' && 
                            key !== 'background_fit' &&
                            key !== 'background_image'
                          );
                          
                          if (slideKeys.length > 0) {
                            safeData.slides = slideKeys.map(key => {
                              const slide = rawContent.slides[key];
                              return {
                                title: (slide && slide.title) || key,
                                content: (slide && slide.content) || '',
                                key: key,
                                images: (slide && Array.isArray(slide.images)) ? [...slide.images] : []
                              };
                            });
                          }
                        }
                      }
                    }
                  } catch (parseError) {
                    console.error('Failed to parse content string:', parseError);
                  }
                }
                
                // If still no slides, create a default slide
                if (safeData.slides.length === 0) {
                  console.warn('No slides could be extracted from pitch deck data');
                  safeData.slides = [{
                    title: 'No Slides Available',
                    content: 'This pitch does not have associated slides or content.',
                    key: 'slide-1',
                    images: []
                  }];
                }
                
                // Set the pitch deck
                setPitchDeck(safeData);

                // Debug log the slide contents
                console.log('Final processed slides:', {
                  slideCount: safeData.slides.length,
                  firstSlideTitle: safeData.slides[0]?.title,
                  allSlideKeys: safeData.slides.map(s => s.key || 'no-key')
                });
              } catch (error) {
                console.error('Error processing pitch deck data:', error);
                // Create a simple fallback with no complex operations
                setPitchDeck({
                  id: 'error',
                  title: 'Error Loading Pitch Deck',
                  slides: [{
                    title: 'Error',
                    content: 'There was an error processing the pitch deck data.',
                    key: 'error-slide',
                    images: []
                  }]
                });
              }
            } else if (publicPitchDeckError) {
              console.log('RPC approach failed, trying uploaded slides utility');
              
              // Try to fetch uploaded slides directly
              const uploadedSlides = await getUploadedSlides(galleryData.pitch_deck_id);
              
              if (uploadedSlides && uploadedSlides.length > 0) {
                console.log(`Found ${uploadedSlides.length} uploaded slides directly from storage`);
                
                // Create a pitch deck object with the uploaded slides
                const uploadedDeck = {
                  id: galleryData.pitch_deck_id,
                  title: galleryData.title || 'Uploaded Pitch Deck',
                  content: {},
                  slides: uploadedSlides,
                  is_uploaded: true
                };
                
                setPitchDeck(uploadedDeck);
                return uploadedDeck;
              }
              
              console.log('RPC approach failed, trying direct query with standard RLS');
              // Try the direct approach, relying on RLS
              const { data: pitchDeckData, error: pitchDeckError } = await supabase
                .from('pitch_decks')
                .select('*')
                .eq('id', galleryData.pitch_deck_id)
                .maybeSingle();

              if (pitchDeckError || !pitchDeckData) {
                console.warn(
                  'Could not fetch pitch deck:', 
                  pitchDeckError, 
                  'for ID:', 
                  galleryData.pitch_deck_id, 
                  'user_type:', 
                  galleryData.user_type || 'unknown'
                );
                
                // Try a fallback with the product_id as a last resort
                const { data: productPitchDeckData, error: productPitchDeckError } = await supabase
                  .from('pitch_decks')
                  .select('*')
                  .eq('product_id', galleryData.product_id)
                  .maybeSingle();
                  
                if (!productPitchDeckError && productPitchDeckData) {
                  console.log('Successfully fetched pitch deck by product_id:', productPitchDeckData?.id);
                  try {
                    // Create a minimal copy with safe defaults
                    const safeData = {
                      id: productPitchDeckData.id || 'fallback',
                      title: productPitchDeckData.name || galleryData.title || 'Pitch Deck',
                      content: {},
                      slides: []
                    };
                    
                    // Safely add content if it exists
                    if (productPitchDeckData.content) {
                      if (typeof productPitchDeckData.content === 'string') {
                        try {
                          safeData.content = JSON.parse(productPitchDeckData.content);
                        } catch (e) {
                          console.error('Content parsing error:', e);
                          safeData.content = {};
                        }
                      } else {
                        safeData.content = productPitchDeckData.content;
                      }
                    }
                    
                    // Safely add slides if they exist
                    if (productPitchDeckData.slides) {
                      if (Array.isArray(productPitchDeckData.slides)) {
                        safeData.slides = [...productPitchDeckData.slides];
                      } else if (typeof productPitchDeckData.slides === 'object') {
                        safeData.slides = [{
                          title: 'Slide Content',
                          content: 'This pitch deck has content in a different format.',
                          key: 'object-slide',
                          images: []
                        }];
                      }
                    }
                    
                    // If slides is empty but content has slides, use those
                    if (safeData.slides.length === 0 && safeData.content && safeData.content.slides) {
                      if (Array.isArray(safeData.content.slides)) {
                        safeData.slides = [...safeData.content.slides];
                      }
                    }
                    
                    // If still no slides, create a default one
                    if (safeData.slides.length === 0) {
                      safeData.slides = [{
                        title: 'No Slides Available',
                        content: 'This pitch deck does not contain any slides.',
                        key: 'no-slides',
                        images: []
                      }];
                    }
                    
                    // Set the pitch deck with no further processing
                    setPitchDeck(safeData);
                  } catch (error) {
                    console.error('Error processing product pitch deck data:', error);
                    // Create a simple fallback with no complex operations
                    setPitchDeck({
                      id: 'error',
                      title: 'Error Loading Pitch Deck',
                      slides: [{
                        title: 'Error',
                        content: 'There was an error processing the pitch deck data.',
                        key: 'error-slide',
                        images: []
                      }]
                    });
                  }
                } else {
                  // All fetching attempts failed, set fallback data
                  setPitchDeck({
                    id: galleryData.pitch_deck_id,
                    title: galleryData.title || 'Pitch Deck',
                    slides: [{
                      title: 'No Slides Available',
                      content: 'The pitch deck content could not be loaded.',
                      key: 'slide-1',
                      images: []
                    }]
                  });
                }
              } else {
                console.log('Successfully fetched pitch deck via direct query:', pitchDeckData?.id);
                try {
                  // Create a minimal copy with safe defaults
                  const safeData = {
                    id: pitchDeckData.id || 'fallback',
                    title: pitchDeckData.name || galleryData.title || 'Pitch Deck',
                    content: {},
                    slides: []
                  };
                  
                  // Safely add content if it exists
                  if (pitchDeckData.content) {
                    if (typeof pitchDeckData.content === 'string') {
                      try {
                        safeData.content = JSON.parse(pitchDeckData.content);
                      } catch (e) {
                        console.error('Content parsing error:', e);
                        safeData.content = {};
                      }
                    } else {
                      safeData.content = pitchDeckData.content;
                    }
                  }
                  
                  // Safely add slides if they exist
                  if (pitchDeckData.slides) {
                    if (Array.isArray(pitchDeckData.slides)) {
                      safeData.slides = [...pitchDeckData.slides];
                    } else if (typeof pitchDeckData.slides === 'object') {
                      safeData.slides = [{
                        title: 'Slide Content',
                        content: 'This pitch deck has content in a different format.',
                        key: 'object-slide',
                        images: []
                      }];
                    }
                  }
                  
                  // If slides is empty but content has slides, use those
                  if (safeData.slides.length === 0 && safeData.content && safeData.content.slides) {
                    if (Array.isArray(safeData.content.slides)) {
                      safeData.slides = [...safeData.content.slides];
                    }
                  }
                  
                  // If still no slides, create a default one
                  if (safeData.slides.length === 0) {
                    safeData.slides = [{
                      title: 'No Slides Available',
                      content: 'This pitch deck does not contain any slides.',
                      key: 'no-slides',
                      images: []
                    }];
                  }
                  
                  // Set the pitch deck with no further processing
                  setPitchDeck(safeData);
                } catch (error) {
                  console.error('Error processing pitch deck data:', error);
                  // Create a simple fallback with no complex operations
                  setPitchDeck({
                    id: 'error',
                    title: 'Error Loading Pitch Deck',
                    slides: [{
                      title: 'Error',
                      content: 'There was an error processing the pitch deck data.',
                      key: 'error-slide',
                      images: []
                    }]
                  });
                }
              }
            }
          } catch (deckError) {
            console.error('Error in pitch deck fetch:', deckError);
            // Create a default/fallback pitch deck
            setPitchDeck({
              id: galleryData.pitch_deck_id,
              title: galleryData.title || 'Pitch Deck',
              slides: [{
                title: 'No Slides Available',
                content: 'The pitch deck content could not be loaded.',
                key: 'slide-1',
                images: []
              }],
              content: {
                slides: [{
                  title: 'No Slides Available',
                  content: 'The pitch deck content could not be loaded.',
                  key: 'slide-1',
                  images: []
                }]
              }
            });
          }
        }
      } catch (deckError) {
        console.error('Error in pitch deck fetch:', deckError);
        // Create a default/fallback pitch deck
        setPitchDeck({
          id: galleryData.pitch_deck_id,
          title: galleryData.title || 'Pitch Deck',
          slides: [{
            title: 'No Slides Available',
            content: 'The pitch deck content could not be loaded.',
            key: 'slide-1',
            images: []
          }],
          content: {
            slides: [{
              title: 'No Slides Available',
              content: 'The pitch deck content could not be loaded.',
              key: 'slide-1',
              images: []
            }]
          }
        });
      }

      // Fetch comments separately
      const { data: commentsData, error: commentsError } = await supabase
        .from('gallery_comments')
        .select('*')
        .eq('gallery_pitch_id', id)
        .order('created_at', { ascending: false });

      if (commentsError) throw commentsError;
      
      // If we have comments, fetch the user profiles for them separately
      if (commentsData && commentsData.length > 0) {
        // Get all unique user IDs from comments
        const userIds = [...new Set(commentsData.map(comment => comment.user_id))];
        
        // Fetch all profiles at once
        const { data: commentProfiles, error: commentProfilesError } = await supabase
          .from('user_profiles')
          .select('user_id, name, photo_url')
          .in('user_id', userIds);
          
        if (!commentProfilesError && commentProfiles) {
          // Map profiles to comments
          commentsData.forEach(comment => {
            comment.user_profile = commentProfiles.find(profile => profile.user_id === comment.user_id) || { 
              name: 'Anonymous',
              photo_url: '',
              user_id: comment.user_id
            };
            
            // Make sure no emails are displayed
            if (!comment.user_profile.name || comment.user_profile.name.includes('@')) {
              comment.user_profile.name = 'Anonymous';
            }
          });
        } else {
          // If we couldn't fetch profiles, set default values
          commentsData.forEach(comment => {
            comment.user_profile = { 
              name: 'Anonymous',
              photo_url: '',
              user_id: comment.user_id
            };
          });
        }
      }
      
      // Set comments to either the fetched data or an empty array
      setComments(commentsData || []);

      // Fetch likes count
      const { data: likesData, error: likesError } = await supabase
        .from('gallery_likes')
        .select('id')
        .eq('gallery_pitch_id', id);

      if (likesError) throw likesError;
      setLikeCount(likesData.length);

    } catch (error) {
      console.error('Error fetching gallery pitch:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const incrementViewCount = useCallback(async () => {
    try {
      // Only continue if we have a valid ID
      if (!id) return;
      
      // First try the RPC function
      try {
        await supabase.rpc('increment_gallery_view_count', { gallery_pitch_id: id });
        
        // Update local state if galleryPitch exists
        setGalleryPitch(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            view_count: (prev.view_count || 0) + 1
          };
        });
      } catch (rpcError) {
        console.log('View count function error, using fallback:', rpcError);
        
        // Fallback: Update the view count directly in the database
        const { data: currentData, error: fetchError } = await supabase
          .from('gallery_pitches')
          .select('view_count')
          .eq('id', id)
          .single();
          
        if (!fetchError && currentData) {
          // Increment view count by 1
          const newViewCount = (currentData.view_count || 0) + 1;
          
          // Update the view_count
          await supabase
            .from('gallery_pitches')
            .update({ view_count: newViewCount })
            .eq('id', id);
          
          // Update local state
          setGalleryPitch(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              view_count: newViewCount
            };
          });
        }
      }
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
  }, [id]);

  const checkUserLike = useCallback(async () => {
    if (!user) return;
    
    try {
      // Use a more error-resilient approach
      try {
        const { data, error } = await supabase
          .from('gallery_likes')
          .select('id')
          .eq('gallery_pitch_id', id)
          .eq('user_id', user.id);

        // If we get data back, check if there are any records
        if (!error && data) {
          setIsLiked(data.length > 0);
        } else {
          throw error;
        }
      } catch (likeError) {
        console.log('Error checking likes, trying alternative approach:', likeError);
        
        // Try alternative approach - check if table exists
        const { data: tablesData, error: tablesError } = await supabase
          .from('pg_tables')
          .select('tablename')
          .eq('tablename', 'gallery_likes')
          .single();
        
        if (tablesError || !tablesData) {
          // Table likely doesn't exist - we'll treat as "not liked"
          setIsLiked(false);
          console.log('gallery_likes table may not exist');
        }
      }
    } catch (error) {
      console.error('Error checking user like:', error);
      // Non-critical error, continue with the page
      setIsLiked(false);
    }
  }, [id, user]);

  useEffect(() => {
    fetchGalleryPitch();
    return () => {
      if (slideInterval.current) clearInterval(slideInterval.current);
    };
  }, [fetchGalleryPitch]);

  // Only increment view count after gallery pitch has been loaded
  useEffect(() => {
    if (galleryPitch && !loading && !viewCountIncremented.current) {
      incrementViewCount();
      viewCountIncremented.current = true;
    }
  }, [galleryPitch, incrementViewCount, loading]);

  useEffect(() => {
    if (user && galleryPitch) {
      checkUserLike();
    }
  }, [user, galleryPitch, checkUserLike]);

  const handleLike = async () => {
    if (!user) {
      navigate('/signin', { state: { from: `/gallery/pitch/${id}` } });
      return;
    }

    try {
      // Check if the table exists first
      try {
        if (isLiked) {
          // Unlike
          const { error } = await supabase
            .from('gallery_likes')
            .delete()
            .eq('gallery_pitch_id', id)
            .eq('user_id', user.id);

          if (error) throw error;
          setIsLiked(false);
          setLikeCount(prev => Math.max(0, prev - 1));
        } else {
          // Like
          const { error } = await supabase
            .from('gallery_likes')
            .insert({
              gallery_pitch_id: id,
              user_id: user.id
            });

          if (error) {
            // If insert fails, the table might not exist
            if (error.code === '42P01') { // undefined_table error
              console.error('gallery_likes table does not exist');
              alert('Like feature is currently unavailable. Please try again later.');
              return;
            }
            throw error;
          }
          setIsLiked(true);
          setLikeCount(prev => prev + 1);
        }
      } catch (likeError) {
        console.error('Error updating like:', likeError);
        alert('Unable to update like status. Please try again later.');
      }
    } catch (error) {
      console.error('Error in like handler:', error);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      navigate('/signin', { state: { from: `/gallery/pitch/${id}` } });
      return;
    }

    if (!newComment.trim()) return;
    
    setCommentSubmitting(true);
    
    try {
      // Create the comment
      const { data: commentData, error: commentError } = await supabase
        .from('gallery_comments')
        .insert({
          gallery_pitch_id: id,
          user_id: user.id,
          comment: newComment.trim()
        })
        .select()
        .single();
        
      if (commentError) throw commentError;
      
      // Try to get the user's profile for display
      let userProfileForComment;
      
      try {
        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .select('name, photo_url')
          .eq('user_id', user.id)
          .single();
          
        if (!profileError && profileData) {
          userProfileForComment = profileData;
          // Make sure email is not displayed
          if (!profileData.name || profileData.name.includes('@')) {
            userProfileForComment.name = 'Anonymous';
          }
        } else {
          // Use anonymous instead of email
          userProfileForComment = {
            name: 'Anonymous',
            photo_url: ''
          };
        }
      } catch (profileError) {
        console.error('Error fetching user profile for comment:', profileError);
        // Fallback to anonymous
        userProfileForComment = {
          name: 'Anonymous',
          photo_url: ''
        };
      }
      
      // Add user profile to the comment
      commentData.user_profile = userProfileForComment;
      
      // Add the new comment to the comments list
      setComments([commentData, ...comments]);
      
      // Clear the comment input
      setNewComment('');
    } catch (error) {
      console.error('Error submitting comment:', error);
      alert('Failed to submit comment. Please try again.');
    } finally {
      setCommentSubmitting(false);
    }
  };

  const startSlideshow = () => {
    setIsPlaying(true);
    slideInterval.current = setInterval(() => {
      setCurrentSlide(prevSlide => {
        const nextSlide = prevSlide + 1;
        if (nextSlide >= slidesArrayRef.current.length) {
          clearInterval(slideInterval.current);
          setIsPlaying(false);
          return prevSlide;
        }
        return nextSlide;
      });
    }, 5000); // 5 seconds per slide
  };

  const stopSlideshow = () => {
    setIsPlaying(false);
    if (slideInterval.current) {
      clearInterval(slideInterval.current);
    }
  };

  const handlePrevSlide = () => {
    stopSlideshow();
    setCurrentSlide(prev => Math.max(0, prev - 1));
  };

  const handleNextSlide = () => {
    stopSlideshow();
    setCurrentSlide(prev => Math.min(slidesArrayRef.current.length - 1, prev + 1));
  };

  // ADDED: Debug function to directly inspect the database structure
  const debugPitchDeckData = async () => {
    if (!id || !galleryPitch?.pitch_deck_id) {
      console.error('Cannot debug: No pitch ID or pitch deck ID available');
      return;
    }
    
    try {
      console.log('Running debug function on pitch deck:', galleryPitch.pitch_deck_id);
      const { data, error } = await supabase
        .rpc('debug_pitch_deck_data', { deck_id: galleryPitch.pitch_deck_id })
        .single();
        
      if (error) {
        console.error('Debug function error:', error);
        return;
      }
      
      console.log('DEBUG OUTPUT -----------------------------');
      console.log('Raw pitch deck structure:', data);
      console.log('Content analysis:', data.content_analysis);
      
      if (data.content_analysis?.is_string) {
        console.log('Content is stored as string, sample:', data.content_analysis.string_sample);
        
        if (data.content_analysis?.is_valid_json) {
          console.log('Content string is valid JSON with type:', data.content_analysis.parsed_type);
          console.log('Has slides in content?', data.content_analysis.has_slides);
          
          if (data.content_analysis.has_slides) {
            console.log('Slides type in content:', data.content_analysis.slides_type);
            
            if (data.content_analysis.slides_type === 'array') {
              console.log('Slides count:', data.content_analysis.slides_count);
              console.log('Sample slide:', data.content_analysis.sample_slide);
            } else if (data.content_analysis.slides_type === 'object') {
              console.log('Slides keys:', data.content_analysis.slides_keys);
              console.log('Sample slide key:', data.content_analysis.sample_slide_key);
            }
          }
        } else {
          console.log('Content is not valid JSON, error:', data.content_analysis.parse_error);
        }
      } else if (data.content_analysis?.is_object) {
        console.log('Content is stored as object with keys:', data.content_analysis.object_keys);
        console.log('Has slides in content?', data.content_analysis.has_slides);
        
        if (data.content_analysis.has_slides) {
          console.log('Slides type in content:', data.content_analysis.slides_type);
          
          if (data.content_analysis.slides_type === 'array') {
            console.log('Slides count:', data.content_analysis.slides_count);
            console.log('Sample slide:', data.content_analysis.sample_slide);
          } else if (data.content_analysis.slides_type === 'object') {
            console.log('Slides keys:', data.content_analysis.slides_keys);
            console.log('Sample slide key:', data.content_analysis.sample_slide_key);
          }
        }
      }
      
      console.log('Has direct slides?', data.has_direct_slides);
      if (data.has_direct_slides) {
        console.log('Direct slides type:', data.direct_slides_type);
        
        if (data.direct_slides_type === 'array') {
          console.log('Direct slides count:', data.direct_slides_count);
          console.log('Direct sample slide:', data.direct_sample_slide);
        } else if (data.direct_slides_type === 'object') {
          console.log('Direct slides keys:', data.direct_slides_keys);
          console.log('Direct sample slide key:', data.direct_sample_slide_key);
        }
      }
      
      console.log('DEBUG OUTPUT END -------------------------');
      
      // Add alert for non-developers to see when debugging
      alert('Debug info has been logged to the console. Please check the browser console (F12) and share the output.');
    } catch (err) {
      console.error('Error in debug function:', err);
    }
  };
  
  // ADDED: Repair function to fix pitch deck slides
  const repairPitchDeckSlides = async () => {
    if (!id || !galleryPitch?.pitch_deck_id) {
      console.error('Cannot repair: No pitch ID or pitch deck ID available');
      return;
    }
    
    try {
      // Confirm before proceeding
      if (!window.confirm('This will attempt to repair the pitch deck slides. Continue?')) {
        return;
      }
      
      console.log('Running repair function on pitch deck:', galleryPitch.pitch_deck_id);
      setLoadingMessage('Repairing pitch deck slides...');
      setFetchingPitchDeck(true);
      
      const { data, error } = await supabase
        .rpc('repair_pitch_deck_slides', { deck_id: galleryPitch.pitch_deck_id })
        .single();
        
      if (error) {
        console.error('Repair function error:', error);
        setFetchingPitchDeck(false);
        alert(`Error repairing slides: ${error.message}`);
        return;
      }
      
      console.log('REPAIR RESULT:', data);
      
      alert(`Repair completed: ${data.status}. ${data.slide_count || 0} slides processed. Refreshing data...`);
      
      // Re-fetch the pitch deck to get the updated data
      await fetchGalleryPitch();
      setFetchingPitchDeck(false);
    } catch (err) {
      console.error('Error in repair function:', err);
      setFetchingPitchDeck(false);
      alert(`Error repairing slides: ${err.message}`);
    }
  };
  
  // Update the debug button component to include repair option
  const DebugButton = () => {
    // Only show in development or with debug flag
    const isDev = process.env.NODE_ENV === 'development';
    const hasDebugFlag = window.location.search.includes('debug=true');
    
    if (!isDev && !hasDebugFlag) return null;
    
    return (
      <Box sx={{ position: 'fixed', right: '10px', bottom: '10px', zIndex: 10000, display: 'flex', flexDirection: 'column', gap: '5px' }}>
        <Button 
          variant="outlined" 
          color="secondary" 
          size="small" 
          onClick={debugPitchDeckData}
        >
          Debug Deck Data
        </Button>
        <Button 
          variant="outlined" 
          color="warning" 
          size="small" 
          onClick={repairPitchDeckSlides}
        >
          Repair Slides
        </Button>
      </Box>
    );
  };

  if (loading || fetchingPitchDeck) {
    return <LoadingScreen message={loadingMessage || "Loading pitch deck..."} />;
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <h2 className="text-2xl font-bold text-red-600">Error</h2>
        <p className="mt-2">{error}</p>
        <Link to="/gallery" className="mt-4 inline-block bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
          Return to Gallery
        </Link>
      </div>
    );
  }

  if (!galleryPitch) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <h2 className="text-2xl font-bold">Pitch Not Found</h2>
        <Link to="/gallery" className="mt-4 inline-block bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
          Return to Gallery
        </Link>
      </div>
    );
  }

  // Determine if we're using the fallback pitch deck
  const isUsingFallbackDeck = pitchDeck && (
    !pitchDeck.slides || 
    pitchDeck.slides.length === 0 ||
    (pitchDeck.slides.length === 1 && 
     (pitchDeck.slides[0].title === 'No Slides Available' || 
      pitchDeck.slides[0].title === 'Error' ||
      !pitchDeck.slides[0].content))
  );
  
  // Log the deck state for debugging
  console.log('Pitch deck state:', {
    hasDeck: !!pitchDeck,
    slideCount: pitchDeck?.slides?.length || 0,
    isUsingFallback: isUsingFallbackDeck,
    firstSlideTitle: pitchDeck?.slides?.[0]?.title || 'none'
  });

  // Handle different slide data structures
  // First try to use the slides property directly from the pitch deck (may be returned by RPC)
  const slides = pitchDeck.slides || pitchDeck.content?.slides || [];
  
  // Extract background image and settings
  const backgroundImage = slides.background_image || pitchDeck.content?.background_image || null;
  const backgroundFit = slides.background_fit || pitchDeck.content?.background_fit || 'cover';
  
  // Ensure we're handling multiple possible slide data structures correctly
  let slidesArray = [];
  
  // Debug the source of the slides
  console.log('Slide data source:', {
    hasDirectSlides: !!pitchDeck?.slides,
    directSlidesType: pitchDeck?.slides ? typeof pitchDeck.slides : 'none',
    hasContentSlides: !!pitchDeck?.content?.slides,
    contentSlidesType: pitchDeck?.content?.slides ? typeof pitchDeck.content.slides : 'none'
  });
  
  if (pitchDeck?.slides) {
    if (Array.isArray(pitchDeck.slides)) {
      console.log('Slides is an array with length:', pitchDeck.slides.length);
      if (pitchDeck.slides.length > 0) {
        slidesArray = pitchDeck.slides.map(slide => ({
          title: slide.title || 'Untitled',
          content: slide.content || '',
          key: slide.key || `slide-${Math.random().toString(36).substr(2, 9)}`,
          images: Array.isArray(slide.images) ? slide.images : []
        }));
      }
    } else if (typeof pitchDeck.slides === 'object') {
      console.log('Slides is an object with keys:', Object.keys(pitchDeck.slides));
      // Handle object-based slides
      const slideKeys = Object.keys(pitchDeck.slides).filter(key => 
        key !== 'slide_order' && 
        key !== 'updated_at' && 
        key !== 'background_fit' &&
        key !== 'background_image'
      );
      
      if (slideKeys.length > 0) {
        slidesArray = slideKeys.map(key => {
          const slide = pitchDeck.slides[key];
          return {
            title: (slide && slide.title) || key,
            content: (slide && slide.content) || '',
            key: key,
            images: (slide && Array.isArray(slide.images)) ? slide.images : []
          };
        });
      }
    } else if (typeof pitchDeck.slides === 'string') {
      try {
        console.log('Slides is a string, attempting to parse as JSON');
        const parsedSlides = JSON.parse(pitchDeck.slides);
        
        if (Array.isArray(parsedSlides)) {
          if (parsedSlides.length > 0) {
            slidesArray = parsedSlides.map(slide => ({
              title: slide.title || 'Untitled',
              content: slide.content || '',
              key: slide.key || `slide-${Math.random().toString(36).substr(2, 9)}`,
              images: Array.isArray(slide.images) ? slide.images : []
            }));
          }
        } else if (typeof parsedSlides === 'object') {
          const slideKeys = Object.keys(parsedSlides).filter(key => 
            key !== 'slide_order' && 
            key !== 'updated_at' && 
            key !== 'background_fit' &&
            key !== 'background_image'
          );
          
          if (slideKeys.length > 0) {
            slidesArray = slideKeys.map(key => {
              const slide = parsedSlides[key];
              return {
                title: (slide && slide.title) || key,
                content: (slide && slide.content) || '',
                key: key,
                images: (slide && Array.isArray(slide.images)) ? slide.images : []
              };
            });
          }
        }
      } catch (e) {
        console.error('Error parsing slides string:', e);
      }
    }
  }
  
  // If no slides from direct slides property, check content.slides
  if (slidesArray.length === 0 && pitchDeck?.content?.slides) {
    console.log('No direct slides, checking content.slides');
    const contentSlides = pitchDeck.content.slides;
    
    if (Array.isArray(contentSlides)) {
      console.log('Content slides is an array with length:', contentSlides.length);
      if (contentSlides.length > 0) {
        slidesArray = contentSlides.map(slide => ({
          title: slide.title || 'Untitled',
          content: slide.content || '',
          key: slide.key || `slide-${Math.random().toString(36).substr(2, 9)}`,
          images: Array.isArray(slide.images) ? slide.images : []
        }));
      }
    } else if (typeof contentSlides === 'object') {
      console.log('Content slides is an object with keys:', Object.keys(contentSlides));
      const slideKeys = Object.keys(contentSlides).filter(key => 
        key !== 'slide_order' && 
        key !== 'updated_at' && 
        key !== 'background_fit' &&
        key !== 'background_image'
      );
      
      if (slideKeys.length > 0) {
        slidesArray = slideKeys.map(key => {
          const slide = contentSlides[key];
          return {
            title: (slide && slide.title) || key,
            content: (slide && slide.content) || '',
            key: key,
            images: (slide && Array.isArray(slide.images)) ? slide.images : []
          };
        });
      }
    }
  }
  
  // If still no slides and content is a string, try to parse it
  if (slidesArray.length === 0 && pitchDeck?.content && typeof pitchDeck.content === 'string') {
    console.log('Attempting to parse content string for slides');
    try {
      const parsedContent = JSON.parse(pitchDeck.content);
      if (parsedContent?.slides) {
        const parsedSlides = parsedContent.slides;
        if (Array.isArray(parsedSlides) && parsedSlides.length > 0) {
          slidesArray = parsedSlides.map(slide => ({
            title: slide.title || 'Untitled',
            content: slide.content || '',
            key: slide.key || `slide-${Math.random().toString(36).substr(2, 9)}`,
            images: Array.isArray(slide.images) ? slide.images : []
          }));
        } else if (typeof parsedSlides === 'object') {
          const slideKeys = Object.keys(parsedSlides).filter(key => 
            key !== 'slide_order' && 
            key !== 'updated_at' && 
            key !== 'background_fit' &&
            key !== 'background_image'
          );
          
          if (slideKeys.length > 0) {
            slidesArray = slideKeys.map(key => {
              const slide = parsedSlides[key];
              return {
                title: (slide && slide.title) || key,
                content: (slide && slide.content) || '',
                key: key,
                images: (slide && Array.isArray(slide.images)) ? slide.images : []
              };
            });
          }
        }
      }
    } catch (e) {
      console.error('Error parsing content string for slides:', e);
    }
  }
  
  // Ensure we have at least one slide
  if (slidesArray.length === 0) {
    console.log('No slides found in any format, creating default slide');
    slidesArray = [{
      title: 'No Slides Available',
      content: 'This pitch deck does not contain any slides.',
      key: 'no-slides',
      images: []
    }];
  } else {
    console.log('Final slides array has', slidesArray.length, 'slides');
    console.log('First slide:', slidesArray[0]);
  }
  
  // Where we set slidesArray the final time before using currentSlideContent
  slidesArrayRef.current = slidesArray;
  
  const currentSlideContent = slidesArray[currentSlide] || null;

  // Function to render slide content 
  const renderSlideContent = () => {
    const slideContent = slides[currentSlide];
    
    if (!slideContent) {
      console.log("No slide content available to render");
      return <div className="text-white">No slide content available</div>;
    }
    
    console.log("Rendering slide with content:", {
      title: slideContent.title,
      contentType: typeof slideContent.content,
      hasImages: slideContent.images && slideContent.images.length > 0
    });
    
    // Special case: PPTX files are no longer supported, show message and download link
    const hasPptxImage = slideContent.images && 
                          Array.isArray(slideContent.images) && 
                          slideContent.images.some(img => 
                            img.url && 
                            (img.url.toLowerCase().endsWith('.pptx') || 
                             img.url.toLowerCase().endsWith('.ppt'))
                          );
                          
    if (hasPptxImage) {
      // Get the PPTX URL
      const pptxImage = slideContent.images.find(img => 
        img.url && 
        (img.url.toLowerCase().endsWith('.pptx') || 
         img.url.toLowerCase().endsWith('.ppt'))
      );
      
      return (
        <div className="w-full h-full p-6 flex flex-col items-center">
          <h2 className="text-xl md:text-2xl font-bold text-white mb-4">{slideContent.title || 'Presentation'}</h2>
          <div className="bg-gray-800 p-8 rounded-lg text-white text-center">
            <svg className="w-12 h-12 text-yellow-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="font-medium">PowerPoint viewing is no longer supported</p>
            <p className="text-sm text-gray-400 mt-2">Please convert this presentation to PDF format for better viewing.</p>
            <p className="text-sm text-gray-400 mt-2">File: {pptxImage.url.split('/').pop()}</p>
            
            <div className="mt-6 flex flex-col space-y-4">
              <a 
                href={pptxImage.url} 
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download Presentation
              </a>
            </div>
          </div>
        </div>
      );
    }
    
    // Handle PDF files (primary case now)
    // Check for direct PDF URL
    if (slideContent.is_pdf && slideContent.pdf_url) {
      return (
        <PDFViewer pdfUrl={slideContent.pdf_url} fullWidth={true} />
      );
    }
    
    // Check for PDF in images array
    const hasPDFFile = slideContent.images && 
                       Array.isArray(slideContent.images) && 
                       slideContent.images.length > 0 && 
                       slideContent.images.some(img => 
                          img.url && img.url.toLowerCase().endsWith('.pdf'));
    
    if (hasPDFFile) {
      const pdfImage = slideContent.images.find(img => img.url && img.url.toLowerCase().endsWith('.pdf'));
      console.log('Found PDF file in images, using PDFViewer:', pdfImage.url);
      
      return (
        <PDFViewer pdfUrl={pdfImage.url} fullWidth={true} />
      );
    }
    
    // Handle presentations that are being converted
    if (slideContent.is_converting) {
      return (
        <div className="w-full h-full p-6 flex flex-col items-center">
          <h2 className="text-xl md:text-2xl font-bold text-white mb-4">{slideContent.title || 'Processing'}</h2>
          <div className="bg-gray-800 p-8 rounded-lg text-white text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white mx-auto mb-4"></div>
            <p>Converting presentation to viewable format...</p>
            <p className="text-sm text-gray-400 mt-4">This may take a minute. The page will automatically update when ready.</p>
          </div>
        </div>
      );
    }
    
    // For regular slides with text content and possibly images
    const hasUploadedImages = slideContent.images && 
                             Array.isArray(slideContent.images) && 
                             slideContent.images.length > 0 && 
                             slideContent.images.some(img => img.url);
    
    // Render standard slides with content and images
    return (
      <div className="w-full h-full p-6 flex flex-col items-center">
        <h2 className="text-xl md:text-2xl font-bold text-white mb-4">{slideContent.title || 'Untitled Slide'}</h2>
        <div className="uploaded-slide flex flex-col items-center">
          {hasUploadedImages && slideContent.images.map((image, idx) => {
            // Only render image files (not pdf, pptx, etc)
            const isImageFile = image.url && 
              (image.url.toLowerCase().endsWith('.jpg') || 
               image.url.toLowerCase().endsWith('.jpeg') || 
               image.url.toLowerCase().endsWith('.png') || 
               image.url.toLowerCase().endsWith('.gif') ||
               image.url.toLowerCase().endsWith('.webp') ||
               image.url.toLowerCase().endsWith('.svg'));
            
            if (!isImageFile) return null;
            
            return (
              <div key={`img-${idx}`} className="uploaded-slide-image my-2">
                <img 
                  src={image.url} 
                  alt={`Slide ${slideContent.title || idx+1}`} 
                  className="max-w-full max-h-80 object-contain rounded-md shadow-sm"
                  onError={(e) => {
                    console.error(`Image ${idx} failed to load:`, image.url);
                    e.target.onerror = null; // Prevent infinite loop
                    e.target.src = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='600' height='400' viewBox='0 0 600 400'%3E%3Crect width='600' height='400' fill='%23CCCCCC'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='24' fill='%23666666'%3EImage Not Available%3C/text%3E%3C/svg%3E`;
                  }}
                />
              </div>
            );
          })}
          
          {slideContent.content && (
            <div className="uploaded-slide-notes mt-4 text-white prose prose-invert max-w-none">
              <p>{slideContent.content}</p>
            </div>
          )}
          
          {!hasUploadedImages && !slideContent.content && (
            <div className="bg-gray-800 p-8 rounded-lg text-white text-center">
              <p>This slide contains no viewable content.</p>
              <p className="text-sm text-gray-400 mt-2">Please upload a PDF file for better viewing experience.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <Link to="/gallery" className="text-blue-600 hover:text-blue-800 flex items-center">
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Gallery
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
        <div className="p-6">
          <div className="flex flex-col md:flex-row justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">{galleryPitch.title}</h1>
              <div className="mt-2 flex items-center text-gray-600">
                <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded mr-2">
                  {galleryPitch.objective}
                </span>
                <span className="text-sm">
                  Posted on {new Date(galleryPitch.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
            
            <div className="mt-4 md:mt-0 flex items-center">
              <button 
                onClick={handleLike}
                className={`flex items-center px-3 py-1.5 rounded-md mr-4 ${
                  isLiked ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <svg className={`w-5 h-5 mr-1 ${isLiked ? 'text-blue-500' : ''}`} 
                     fill={isLiked ? "currentColor" : "none"} 
                     stroke="currentColor" 
                     viewBox="0 0 24 24">
                  <path strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth="2" 
                        d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905a3.61 3.61 0 01-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                </svg>
                {likeCount} {likeCount === 1 ? 'Like' : 'Likes'}
              </button>
              
              <div className="flex items-center text-gray-600">
                <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                {galleryPitch.view_count || 0} {(galleryPitch.view_count === 1) ? 'View' : 'Views'}
              </div>
            </div>
          </div>
          
          {isUsingFallbackDeck && !DISABLE_WARNING_MESSAGES && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-md text-amber-800">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <p className="font-medium">
                    The pitch deck content could not be loaded. You can still view the pitch information but slides may not be available.
                  </p>
                  {galleryPitch.user_type === 'service' && (
                    <p className="mt-1 text-sm">
                      Note: Service profile pitches currently have limited slide functionality. The pitch information is still accessible.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <div className="flex flex-col md:flex-row items-start md:items-center mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center mb-4 md:mb-0 md:mr-8">
              <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden mr-3">
                {userProfile?.photo_url ? (
                  <img 
                    src={userProfile.photo_url} 
                    alt={userProfile.name || 'User'}
                    className="w-full h-full object-cover" 
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/40?text=U';
                    }}
                  />
                ) : (
                  <span className="text-lg text-gray-600">
                    {userProfile?.name ? userProfile.name.charAt(0).toUpperCase() : 'U'}
                  </span>
                )}
              </div>
              <div>
                <h3 className="font-medium">{userProfile?.name || 'Unknown User'}</h3>
                <p className="text-sm text-gray-600">
                  {userProfile?.job_title || ''}
                  {userProfile?.job_title && userProfile?.company ? ' at ' : ''}
                  {userProfile?.company || ''}
                </p>
              </div>
            </div>
            
            <div className="flex flex-col md:flex-row md:space-x-6">
              <div className="mb-2 md:mb-0">
                <span className="text-xs text-gray-500 block">Product</span>
                {user?.id === galleryPitch.user_id ? (
                  <span className={`font-medium ${galleryPitch.products?.name === 'Product information unavailable' || galleryPitch.products?.name === 'No product associated' ? 'text-gray-500 italic' : ''}`}>
                    {galleryPitch.products?.name || 'Unknown Product'}
                  </span>
                ) : (
                  <span className="font-medium">
                    {galleryPitch.products?.name && 
                     galleryPitch.products.name !== 'Product information unavailable' && 
                     galleryPitch.products.name !== 'No product associated' ? 
                     galleryPitch.products.name : 'View limited for non-owners'}
                  </span>
                )}
              </div>
              <div className="mb-2 md:mb-0">
                <span className="text-xs text-gray-500 block">Stage</span>
                {user?.id === galleryPitch.user_id ? (
                  <span className={`font-medium ${galleryPitch.products?.development_stage === 'Not specified' ? 'text-gray-500 italic' : ''}`}>
                    {galleryPitch.products?.development_stage || 'Not specified'}
                  </span>
                ) : (
                  <span className="font-medium">
                    {galleryPitch.products?.development_stage && 
                     galleryPitch.products.development_stage !== 'Not specified' ? 
                     galleryPitch.products.development_stage : 'View limited for non-owners'}
                  </span>
                )}
              </div>
              <div>
                <span className="text-xs text-gray-500 block">Category</span>
                {user?.id === galleryPitch.user_id ? (
                  <span className={`font-medium ${galleryPitch.products?.product_category === 'Not specified' ? 'text-gray-500 italic' : ''}`}>
                    {galleryPitch.products?.product_category || 'Not specified'}
                  </span>
                ) : (
                  <span className="font-medium">
                    {galleryPitch.products?.product_category && 
                     galleryPitch.products.product_category !== 'Not specified' ? 
                     galleryPitch.products.product_category : 'View limited for non-owners'}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {/* Add warning for missing product information for service profiles */}
          {galleryPitch.user_type === 'service' && 
            (!galleryPitch.product_id || galleryPitch.products?.name === 'No product associated' || galleryPitch.products?.name === 'Product information unavailable') && (
              <div className="p-3 bg-blue-50 text-blue-700 rounded-md mb-6 text-sm">
                <p>
                  <span className="font-medium">Note:</span> Service profiles may not have associated product information. This is expected behavior.
                </p>
              </div>
            )}
          
          <p className="text-gray-700 mb-6">{galleryPitch.description}</p>
        </div>
        
        <div className="bg-gray-900 p-6 relative">
          {isDisplayingPDF ? (
            // Render just the PDF viewer without any slide container
            <div className="min-h-[700px]">
              {currentSlideContent ? renderSlideContent() : <div className="text-white">No PDF content available</div>}
            </div>
          ) : (
            // Regular slide view with controls
            <>
              <div 
                className="relative aspect-[16/9] bg-black flex items-center justify-center overflow-hidden mb-4"
                style={{
                  backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
                  backgroundSize: backgroundFit,
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                }}
              >
                {currentSlideContent ? (
                  renderSlideContent()
                ) : (
                  <div className="text-white">No slide content available</div>
                )}
              </div>
              
              <div className="flex justify-between items-center">
                <div className="text-white">
                  Slide {currentSlide + 1} of {slidesArray.length}
                </div>
                
                <div className="flex space-x-2">
                  <button
                    onClick={handlePrevSlide}
                    disabled={currentSlide === 0}
                    className="p-2 rounded-full bg-gray-800 text-white hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Previous slide"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  
                  {isPlaying ? (
                    <button
                      onClick={stopSlideshow}
                      className="p-2 rounded-full bg-gray-800 text-white hover:bg-gray-700"
                      aria-label="Pause slideshow"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                  ) : (
                    <button
                      onClick={startSlideshow}
                      disabled={slidesArray.length <= 1}
                      className="p-2 rounded-full bg-gray-800 text-white hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Play slideshow"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                  )}
                  
                  <button
                    onClick={handleNextSlide}
                    disabled={currentSlide === slidesArray.length - 1}
                    className="p-2 rounded-full bg-gray-800 text-white hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Next slide"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
              
              {slidesArray.length > 1 && (
                <div className="mt-4 grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                  {slidesArray.map((slide, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentSlide(idx)}
                      className={`aspect-video bg-gray-800 p-1 rounded-md overflow-hidden ${currentSlide === idx ? 'ring-2 ring-blue-500' : ''}`}
                    >
                      {slide.images && slide.images[0] && slide.images[0].url && (
                        <div className="w-full h-full bg-cover bg-center"
                             style={{ backgroundImage: `url(${slide.images[0].url})` }}>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Comments Section */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-6">
            Comments ({comments.length})
          </h2>
          
          {user ? (
            <form onSubmit={handleCommentSubmit} className="mb-8">
              <div className="flex">
                <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden mr-4">
                  {user.user_metadata?.avatar_url ? (
                    <img 
                      src={user.user_metadata.avatar_url} 
                      alt={user.user_metadata.full_name}
                      className="w-full h-full object-cover" 
                    />
                  ) : (
                    <span className="text-lg text-gray-600">
                      {user.email?.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="flex-grow">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    required
                  />
                  <div className="mt-2 flex justify-end">
                    <button
                      type="submit"
                      disabled={commentSubmitting || !newComment.trim()}
                      className={`px-4 py-2 rounded ${
                        commentSubmitting || !newComment.trim()
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {commentSubmitting ? 'Posting...' : 'Post Comment'}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          ) : (
            <div className="mb-8 p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-gray-700 mb-2">
                Please sign in to add a comment.
              </p>
              <Link
                to={`/signin?redirect=/gallery/pitch/${id}`}
                className="inline-block bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Sign In
              </Link>
            </div>
          )}
          
          {comments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No comments yet. Be the first to share your thoughts!
            </div>
          ) : (
            <div className="space-y-6">
              {comments.map((comment) => (
                <div key={comment.id} className="flex">
                  <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden mr-4">
                    {comment.user_profile?.photo_url ? (
                      <img 
                        src={comment.user_profile.photo_url} 
                        alt={comment.user_profile.name || 'User'}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/40?text=?';
                        }}
                      />
                    ) : (
                      <span className="text-lg text-gray-600">
                        {comment.user_profile?.name ? comment.user_profile.name.charAt(0).toUpperCase() : '?'}
                      </span>
                    )}
                  </div>
                  <div className="flex-grow">
                    <div className="flex items-center mb-1">
                      <h4 className="font-medium mr-2">{comment.user_profile?.name || 'Unknown User'}</h4>
                      <span className="text-xs text-gray-500">
                        {new Date(comment.created_at).toLocaleDateString()} at {new Date(comment.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="text-gray-700">{comment.comment}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Add the debug button at the end of your component */}
      <DebugButton />
    </div>
  );
};

export default GalleryViewPage; 